import { type DateRange } from "react-day-picker";
import {
  endOfQuarter,
  endOfYear,
  startOfQuarter,
  startOfYear,
  subQuarters,
  subYears,
} from "date-fns";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";

export type PresetOption = {
  label: string;
  getValue: () => DateRange;
  disabled?: boolean;
};

const getDefaultPresets = (): PresetOption[] => [
  {
    label: m.date_this_quarter(),
    getValue: () => ({
      from: startOfQuarter(new Date()),
      to: endOfQuarter(new Date()),
    }),
  },
  {
    label: m.date_last_quarter(),
    getValue: () => ({
      from: startOfQuarter(subQuarters(new Date(), 1)),
      to: endOfQuarter(subQuarters(new Date(), 1)),
    }),
  },
  {
    label: m.date_this_year(),
    getValue: () => ({
      from: startOfYear(new Date()),
      to: endOfYear(new Date()),
    }),
  },
  {
    label: m.date_last_year(),
    getValue: () => ({
      from: startOfYear(subYears(new Date(), 1)),
      to: endOfYear(subYears(new Date(), 1)),
    }),
  },
];

export const PresetSelector = ({
  onSelectPreset,
  selectedPreset,
  presets: customPresets,
  shouldDisablePreset,
}: {
  onSelectPreset: (dateRange?: DateRange) => void;
  selectedPreset?: DateRange | null;
  presets?: PresetOption[];
  shouldDisablePreset?: (preset: PresetOption) => boolean;
}) => {
  const presets = customPresets ?? getDefaultPresets();
  const isPresetSelected = (presetValue: DateRange) => {
    if (!selectedPreset || isFalsy(presetValue)) return false;

    const fromSelected = selectedPreset.from;
    const toSelected = selectedPreset.to;
    const fromPreset = presetValue.from;
    const toPreset = presetValue.to;

    return isTruthy(
      fromSelected &&
        toSelected &&
        fromPreset &&
        toPreset &&
        fromSelected.getTime() === fromPreset.getTime() &&
        toSelected.getTime() === toPreset.getTime(),
    );
  };

  const isPresetDisabled = (preset: PresetOption) => {
    if (preset.disabled) return true;

    if (shouldDisablePreset) {
      return shouldDisablePreset(preset);
    }

    return false;
  };

  const handlePresetClick = (preset: (typeof presets)[0]) => {
    if (isPresetDisabled(preset)) return;

    const presetValue = preset.getValue();
    const selected = isPresetSelected(presetValue);

    if (selected) {
      onSelectPreset(undefined);
    } else {
      onSelectPreset(presetValue);
    }
  };

  return (
    <div className="grid grid-cols-2 lg:flex lg:flex-col border-border/primary border-r border-border p-2 py-1 gap-1 ">
      {presets.map((preset) => {
        const presetValue = preset.getValue();
        const selected = isPresetSelected(presetValue);
        const disabled = isPresetDisabled(preset);

        return (
          <ThemedButton
            className={cnTwMerge(
              "body-base-medium text-text/primary hover:bg-background/component p-2",
              selected ? "bg-background/component" : undefined,
              disabled
                ? "text-text/disabled cursor-not-allowed opacity-50"
                : undefined,
            )}
            dataTestId={`preset-${preset.label}`}
            disabled={disabled}
            key={preset.label}
            onClick={() => handlePresetClick(preset)}
            variant="tertiary"
          >
            {preset.label}
          </ThemedButton>
        );
      })}
    </div>
  );
};
